import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, X } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Full name", required: true },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "suburb", label: "Suburb" },
  { key: "notes", label: "Notes" },
];

export default function ImportCustomersDialog({ open, onOpenChange, onImported }) {
  const fileInput = useRef(null);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setFileName(""); setHeaders([]); setRows([]); setMapping({});
    if (fileInput.current) fileInput.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/customers/import/preview", fd);
      setHeaders(data.headers);
      setRows(data.rows);
      setMapping(data.suggested || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the CSV file");
      reset();
    } finally {
      setLoading(false);
    }
  };

  const setField = (key) => (e) => setMapping({ ...mapping, [key]: e.target.value });

  const validCount = mapping.name
    ? rows.filter((r) => (r[mapping.name] || "").trim()).length
    : 0;

  const doImport = async () => {
    if (!mapping.name) return toast.error("Please choose which column is the client name");
    const customers = rows
      .map((r) => ({
        name: (r[mapping.name] || "").trim(),
        email: mapping.email ? (r[mapping.email] || "").trim() : "",
        phone: mapping.phone ? (r[mapping.phone] || "").trim() : "",
        address: mapping.address ? (r[mapping.address] || "").trim() : "",
        suburb: mapping.suburb ? (r[mapping.suburb] || "").trim() : "",
        notes: mapping.notes ? (r[mapping.notes] || "").trim() : "",
      }))
      .filter((c) => c.name);
    if (customers.length === 0) return toast.error("No rows with a client name were found");
    setImporting(true);
    try {
      const { data } = await api.post("/customers/import", {
        customers,
        skip_duplicates: skipDuplicates,
        filename: fileName,
      });
      const parts = [`${data.imported} imported`];
      if (data.skipped_duplicate) parts.push(`${data.skipped_duplicate} duplicates skipped`);
      if (data.skipped_no_name) parts.push(`${data.skipped_no_name} without a name skipped`);
      toast.success(parts.join(" · "));
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const previewRows = rows.slice(0, 4);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Import Clients from CSV</DialogTitle>
        </DialogHeader>

        {headers.length === 0 ? (
          <div className="py-6">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              data-testid="import-dropzone"
              className="w-full border border-dashed border-brand/50 rounded-sm py-12 flex flex-col items-center gap-3 text-muted-foreground hover:border-brand hover:bg-accent/40 transition-colors"
            >
              <Upload className="w-8 h-8" strokeWidth={1.5} />
              <div className="text-sm">
                {loading ? "Reading file…" : "Click to choose a CSV file"}
              </div>
              <div className="text-xs">Export your client list from Maidily as CSV, then upload it here.</div>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="hidden"
              data-testid="import-file-input"
            />
          </div>
        ) : (
          <div className="space-y-6 py-2 max-h-[65vh] overflow-y-auto pr-1">
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="w-4 h-4 text-brand" />
              <span className="font-medium">{fileName}</span>
              <span className="text-muted-foreground">· {rows.length} rows found</span>
              <button onClick={reset} className="ml-auto text-muted-foreground hover:text-destructive" data-testid="import-clear">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <Label className="label-caps text-muted-foreground">Match your columns</Label>
              <div className="grid grid-cols-2 gap-4 mt-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-sm">
                      {f.label}{f.required && <span className="text-brand"> *</span>}
                    </Label>
                    <select
                      value={mapping[f.key] || ""}
                      onChange={setField(f.key)}
                      data-testid={`map-${f.key}`}
                      className="w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand"
                    >
                      <option value="">— Skip —</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {mapping.name && previewRows.length > 0 && (
              <div>
                <Label className="label-caps text-muted-foreground">Preview</Label>
                <div className="mt-3 border border-border/60 rounded-sm overflow-hidden text-sm">
                  <div className="grid grid-cols-3 bg-accent/40 label-caps text-xs px-3 py-2">
                    <div>Name</div><div>Phone</div><div>Suburb</div>
                  </div>
                  {previewRows.map((r, i) => (
                    <div key={i} className="grid grid-cols-3 px-3 py-2 border-t border-border/50" data-testid={`import-preview-row-${i}`}>
                      <div className="truncate pr-2">{r[mapping.name] || "—"}</div>
                      <div className="truncate pr-2">{mapping.phone ? (r[mapping.phone] || "—") : "—"}</div>
                      <div className="truncate">{mapping.suburb ? (r[mapping.suburb] || "—") : "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
                data-testid="import-skip-duplicates"
              />
              Skip clients that already exist (matched by email or phone)
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="rounded-sm">Cancel</Button>
          {headers.length > 0 && (
            <Button onClick={doImport} disabled={importing || !mapping.name} data-testid="confirm-import-button" className="rounded-sm">
              {importing ? "Importing…" : `Import ${validCount} Client${validCount === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
